/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, Configuration } from "@azure/msal-browser";
import type { ProtectedResources } from "./types/UserTypes";
import { resolveAuthRedirectUri } from "./utils/authRedirect";

const isDev = process.env.NODE_ENV === "development";
/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

// export const b2cPolicies = {
//   names: {
//     signUpSignIn: "B2C_1_SiInSiUp_v2",
//     editProfile: "B2C_1_SiPe",
//   },
//   authorities: {
//     signUpSignIn: {
//       authority:
//         "https://BobSampleTest.b2clogin.com/BobSampleTest.onmicrosoft.com/B2C_1_SiInSiUp_v2",
//     },
//     editProfile: {
//       authority:
//         "https://BobSampleTest.b2clogin.com/BobSampleTest.onmicrosoft.com/B2C_1_SiPe",
//     },
//   },
//   authorityDomain: "LandAgility.b2clogin.com",
// };

// External ID user flows (replace B2C policies)
export const externalIdFlows = {
  names: {
    signUpSignIn: "SignUpSignIn",
  },
  authorities: {
    signUpSignIn: {
      authority:
        "https://landagility.ciamlogin.com/07e3fbd3-5724-4b31-b6a9-d18a1cdde1f8/"
    },
  },
  authorityDomain: "landagility.ciamlogin.com",
};

export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_MSAL_CLIENT_ID || "c2c5f759-3fc6-4b84-9799-dedfebb3ed88",
    authority: externalIdFlows.authorities.signUpSignIn.authority,
    knownAuthorities: [externalIdFlows.authorityDomain],
    redirectUri: resolveAuthRedirectUri(
      process.env.REACT_APP_REDIRECT_URI || "http://localhost:3000",
      typeof window === "undefined" ? "" : window.location.origin,
      process.env.REACT_APP_WARMUP_ORIGIN,
    ),
    postLogoutRedirectUri: "/timeentry",
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "localStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      // 🔇 Quiet in prod, helpful in dev
      logLevel: isDev ? LogLevel.Info : LogLevel.Warning,

      // 🔐 NEVER log PII
      piiLoggingEnabled: false,

      loggerCallback: (level: LogLevel, message: string): void => {
        // Extra guard: prod only logs warnings/errors
        if (!isDev && level < LogLevel.Warning) return;

        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            if (isDev) console.info(message);
            break;
          default:
            break;
        }
      },
    },
  },
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
// export const graphConfig = {
//     graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
// };

export const protectedResources: ProtectedResources = {
  baseURL: {
    endpoint: process.env.REACT_APP_API_BASE_URL
      ? `${process.env.REACT_APP_API_BASE_URL}/api`
      : "https://localhost:44396/api",
    scopes: {
      access: [
        process.env.REACT_APP_API_SCOPE || "api://3c8a5715-3bd2-4c5c-8a1b-cd9f2fd7f5b6/api.access",
      ],
    },
  },
};

// export const loginRequest = {
//   scopes: [
//     "openid",
//     "profile",
//     "email",
//     ...protectedResources.baseURL.scopes.access,
//   ],
// };
export const loginRequest: { scopes: string[] } = {
  scopes: [
    "openid",
    "profile",
    "email",
    ...protectedResources.baseURL.scopes.access,
  ],
};
